"use client"

import type { ReactNode } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  BodyFigureIcon,
  ChecklistIcon,
  ImpactAreaIcon,
  ShieldCheckIcon,
  ReportIcon,
  SearchIcon,
} from "@/components/ui/icons"

const STUDY_LINK_CLASS =
  "underline decoration-neutral-600/40 underline-offset-2"

const iconClass = "size-8 text-neutral-900"

/** Per-page card icons. Copy lives in the `tests` namespace, keyed by prefix;
 *  only the icon + layout structure is in code. */
const CARD_ICONS: Record<string, ReactNode[]> = {
  prostateMri: [
    <ChecklistIcon key="checklist" className={iconClass} />,
    <ImpactAreaIcon key="impact" className={iconClass} />,
    <ShieldCheckIcon key="shield" className={iconClass} />,
  ],
  ovariesMri: [
    <SearchIcon key="search" className={iconClass} />,
    <ChecklistIcon key="checklist" className={iconClass} />,
    <ReportIcon key="report" className={iconClass} />,
  ],
}

/** Primary-source links for studies cited in the copy. Surfaced via next-intl
 *  rich-text `<prime>` / `<precision>` tags in the messages; tagless strings
 *  render unchanged. Opens in a new tab. */
const STUDY_LINKS = {
  prime: (chunks: ReactNode) => (
    <a
      href="https://pubmed.ncbi.nlm.nih.gov/40928788/"
      target="_blank"
      rel="noopener noreferrer"
      className={STUDY_LINK_CLASS}
    >
      {chunks}
    </a>
  ),
  precision: (chunks: ReactNode) => (
    <a
      href="https://pubmed.ncbi.nlm.nih.gov/30089067/"
      target="_blank"
      rel="noopener noreferrer"
      className={STUDY_LINK_CLASS}
    >
      {chunks}
    </a>
  ),
}

type Translator = ((key: string) => string) & {
  rich: (
    key: string,
    values: Record<string, (chunks: ReactNode) => ReactNode>
  ) => ReactNode
}

/**
 * "Why our MRI" — how Ahead's focused MRI protocol differs from the
 * alternatives. Eyebrow icon + two-tone centred heading + subhead, then three
 * white evidence cards. Copy lives in the `tests` namespace keyed by
 * `<prefix>Why*`; icons + layout are in code. Rendered after the
 * "<test>, explained" section on the gender-health MRI pages.
 */
export function MriProtocolSection({ keyPrefix }: { keyPrefix: string }) {
  const t = useTranslations("tests") as unknown as Translator
  const icons = CARD_ICONS[keyPrefix] ?? CARD_ICONS.prostateMri
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <BodyFigureIcon className="mx-auto size-12 text-neutral-900" />
          <h2 className="type-display-md mt-5 text-neutral-900">
            {t(`${keyPrefix}WhyHeading1`)}
            <br />
            <span className="text-bright-blue-600">
              {t(`${keyPrefix}WhyHeading2`)}
            </span>
          </h2>
          <p className="type-body-lg mt-4 text-neutral-600">
            {t.rich(`${keyPrefix}WhySubhead`, STUDY_LINKS)}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 md:grid-cols-3 md:gap-8">
          {[1, 2, 3].map((n, i) => (
            <div
              key={n}
              className="rounded-[16px] bg-white px-6 py-10 text-center md:px-8 md:py-12"
            >
              <div className="flex justify-center">{icons[i]}</div>
              <h3 className="type-title-lg mt-6 text-neutral-900">
                {t(`${keyPrefix}WhyCard${n}Title`)}
              </h3>
              <p className="type-body-xl mt-4 text-neutral-600">
                {t.rich(`${keyPrefix}WhyCard${n}Body`, STUDY_LINKS)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
